<template>
  <div>
    <BasePaymentRequestsTable
      v-model:selection-mode="selectionMode"
      :query="query"
      data-test="payment-requests-processing-table"
      :is-active-tab="isActiveTab"
      :bulk-actions="bulkActions"
      :batch-ids="batchSelection.selectedBatchIds.value"
      :enabled-row-selection-modes="enabledRowSelectionModes"
    >
      <template #action="{ row }">
        <PaymentRequestActionColumnProcessing
          :row="row"
          @payment-request-updated="$emit('payment-request-updated')"
        />
      </template>
      <template #filters>
        <BatchFilterMultiSelect
          data-test="payment-requests-processing-batch-filter"
          :batches="batchSelection.batches.value"
          :selected-batch-ids="batchSelection.selectedBatchIds.value"
          @update:selected-batch-ids="handleBatchSelectionUpdated"
        />
      </template>
    </BasePaymentRequestsTable>

    <ConfirmationModal
      :show="confirmationModal.isVisible.value"
      :title="confirmationModal.config.value?.title || $t('Confirm Action')"
      :description="confirmationModal.config.value?.message"
      :cancel-text="confirmationModal.config.value?.cancelText || $t('Cancel')"
      :confirm-text="confirmationModal.config.value?.confirmText || $t('Confirm')"
      :loading="confirmationModal.isLoading.value"
      @confirm="confirmationModal.handleConfirm"
      @cancel="confirmationModal.handleCancel"
    />
  </div>
</template>

<script lang="ts" setup>
import { TypedQueryDocumentNode } from 'graphql/index'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { watch, ref, computed } from 'vue'
import BasePaymentRequestsTable from './BasePaymentRequestsTable.vue'
import BatchFilterMultiSelect from './BatchFilterMultiSelect.vue'
import { useBatchSelection } from './composables/useBatchSelection'
import { buildBatchActionInputs, downloadFromUrls } from './composables/batchActions'
import { graphql } from '@/gql'
import { $t } from '@/composables/translation'
import { useToast } from '@/composables/useToast'
import { useConfirmationModal } from '@/composables/useConfirmationModal'
import { DataTableBulkAction } from '@/components/GraphqlDataTable/types'
import { RowSelectionMode } from '@/components/GraphqlDataTable/enums'
import { RunBatchActionMutation } from '@/components/GraphQL/mutations/Batch/RunBatchActionMutation'
import { BatchActionType, BatchItemStatus } from '@/gql/graphql'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'
import ConfirmationModal from '@/components/Common/ConfirmationModal.vue'
import PaymentRequestActionColumnProcessing from '@/components/GraphqlDataTable/columns/PaymentRequest/Partner/PaymentRequestActionColumnProcessing.vue'

const selectionMode = ref<RowSelectionMode>(RowSelectionMode.PAGE)

defineProps<{ isActiveTab?: boolean }>()

const query = graphql(`
  query PartnerPaymentRequestsProcessingQuery(
    $accounts: [ID!]
    $search: String
    $first: Int!
    $page: Int
    $orderBy: [PaymentRequestOrderByClauseInput!]
    $timesheetPeriod: DateRangeInput
    $hasExpenses: Boolean
    $jobs: [ID!]
    $clients: [ID!]
    $batchIds: [ID!]
  ) {
    paymentRequests(
      accounts: $accounts
      search: $search
      first: $first
      page: $page
      orderBy: $orderBy
      timesheetPeriod: $timesheetPeriod
      hasExpenses: $hasExpenses
      jobs: $jobs
      clients: $clients
      batchIds: $batchIds
      statuses: [APPROVED]
      hasBatch: true
      isPayrolled: false
    ) {
      data {
        ...PaymentRequestBatchCreatedAtColumn
        ...PaymentRequestBatchColumn
        ...PaymentRequestPrIdColumn
        ...PaymentRequestWorkerColumn
        ...PaymentRequestClientColumn
        ...PaymentRequestTimesheetPeriodColumn
        ...PaymentRequestRateColumn
        ...PaymentRequestUnitsColumn
        ...PaymentRequestExpensesColumn
        ...PaymentRequestJobColumn
        ...PaymentRequestAssignmentCompanyColumn
        ...PaymentRequestWorkLocationColumn
        ...PaymentRequestActionColumnProcessing
      }
    }
  }
`) as TypedQueryDocumentNode

const batchQuery = graphql(`
  query BatchFilterProcessingQuery(
    $accounts: [ID!]
    $containsItemsWithStatus: [BatchItemStatus!]
    $first: Int!
  ) {
    batches(accounts: $accounts, containsItemsWithStatus: $containsItemsWithStatus, first: $first) {
      paginatorInfo {
        total
      }
      data {
        id
        name
        type
        processingItemsCount: itemsCountByStatus(status: [PROCESSING])
      }
    }
  }
`) as TypedQueryDocumentNode

const { result: batchResult } = useQuery(
  batchQuery,
  () => ({
    accounts: [currentAccount.globalId],
    containsItemsWithStatus: [BatchItemStatus.Processing],
    first: PER_PAGE_MAXIMUM,
  }),
  {
    clientId: 'publicApi',
  },
)

const batchSelection = useBatchSelection()

watch(batchResult, (newResult) => {
  if (newResult?.batches) {
    batchSelection.updateBatches(newResult.batches.data)
  }
})

const enabledRowSelectionModes = computed<RowSelectionMode[]>(() =>
  batchSelection.selectedBatchIds.value.length > 0
    ? [RowSelectionMode.ALL]
    : [RowSelectionMode.PAGE],
)

const handleBatchSelectionUpdated = async (ids: string[]) => {
  const current = new Set(Object.keys(batchSelection.selectedBatches.value))
  current.forEach((id) => {
    if (!ids.includes(id)) batchSelection.deselectBatch(id)
  })
  ids.forEach((id) => batchSelection.selectBatch(id))
  selectionMode.value =
    batchSelection.selectedBatchIds.value.length > 0 ? RowSelectionMode.ALL : RowSelectionMode.PAGE
}

const toast = useToast()

const emit = defineEmits<{
  'payment-request-updated': []
}>()

const confirmationModal = useConfirmationModal()

const { mutate: runBatchAction } = useMutation(RunBatchActionMutation, {
  clientId: 'publicApi',
})

const bulkActions = computed<DataTableBulkAction[]>(() => [
  {
    name: $t('Undo processing'),
    icon: 'undo' as any,
    type: 'error',
    dataTest: 'undo-processing-bulk',
    action: async (selectedRows, scope, blackList, totalCount) => {
      const affectedItemsCount =
        scope === 'full-scope'
          ? Math.max(0, (totalCount || 0) - (blackList?.length || 0))
          : Object.keys(selectedRows).length

      const batchInputs = buildBatchActionInputs({
        selectedRows,
        excludedIds: blackList || [],
        isFullScope: scope === 'full-scope',
        selectedBatchIds: batchSelection.selectedBatchIds.value,
        action: BatchActionType.Undo,
        deleteIfEmptied: true,
        status: scope === 'full-scope' ? BatchItemStatus.Processing : undefined,
      })
      const count = affectedItemsCount

      confirmationModal.showConfirmation({
        title: $t('Undo Processing'),
        message: $t(
          'Are you sure you want to undo processing for :count payment requests? This will remove the selected items from their batch(es).',
          { count },
        ),
        confirmText: $t('Yes, undo processing'),
        cancelText: $t('Cancel'),
        type: 'error',
        onConfirm: async () => {
          try {
            await Promise.all(
              batchInputs.map((input) =>
                runBatchAction({ input }).then(() => ({
                  batchId: input.batch,
                  scope: input.scope,
                })),
              ),
            )
            toast.success(
              $t('Successfully undone processing for :count payment requests', { count }),
            )

            emit('payment-request-updated')
          } catch (error) {
            throw new Error($t('Failed to undo payment request processing'))
          }
        },
      })
    },
  },
  {
    name: $t('Confirm payrolled'),
    icon: 'checkmark-outline' as any,
    type: 'secondary',
    scope: 'full',
    dataTest: 'confirm-payrolled-bulk',
    action: async (selectedRows, scope, excludedIds, totalCount) => {
      const paymentRequestIds = Object.keys(selectedRows)

      try {
        const inputs = buildBatchActionInputs({
          selectedRows,
          excludedIds: excludedIds || [],
          isFullScope: scope === 'full-scope',
          selectedBatchIds: batchSelection.selectedBatchIds.value,
          action: BatchActionType.Process,
          deleteIfEmptied: false,
          status: scope === 'full-scope' ? BatchItemStatus.Processing : undefined,
        })

        await Promise.all(inputs.map((input) => runBatchAction({ input })))

        const count =
          scope === 'full-scope'
            ? Math.max(0, (totalCount || 0) - ((excludedIds as any)?.length || 0))
            : paymentRequestIds.length

        toast.success($t('Successfully marked :count payment requests as payrolled', { count }))

        emit('payment-request-updated')
      } catch (error) {
        throw new Error($t('Failed to mark payment requests as payrolled'))
      }
    },
  },
  {
    name: $t('Export'),
    icon: 'download' as any,
    type: 'primary',
    scope: 'full',
    dataTest: 'export-bulk',
    action: async (selectedRows, scope, excludedIds, totalCount) => {
      const paymentRequestIds = Object.keys(selectedRows)

      try {
        const inputs = buildBatchActionInputs({
          selectedRows,
          excludedIds: excludedIds || [],
          isFullScope: scope === 'full-scope',
          selectedBatchIds: batchSelection.selectedBatchIds.value,
          action: BatchActionType.Export,
          deleteIfEmptied: false,
          status: scope === 'full-scope' ? BatchItemStatus.Processing : undefined,
        })
        const urls = await Promise.all(
          inputs.map((input) =>
            runBatchAction({ input }).then((res) => res?.data?.runBatchAction?.resultUrl),
          ),
        )

        await downloadFromUrls(urls)

        const count =
          scope === 'full-scope'
            ? Math.max(0, (totalCount || 0) - ((excludedIds as any)?.length || 0))
            : paymentRequestIds.length
        toast.success($t('Successfully exported :count payment requests', { count }))
      } catch (error) {
        toast.error($t('Failed to export payment requests. Please try again.'))
      }
    },
  },
])
</script>

<style scoped></style>
